import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AppShell, TopHeader } from "../components/AppShell";
import { Button, Card, Pill, SectionLabel } from "../components/ui";
import { Ic } from "../components/icons";
import { useNav } from "../context/NavContext";
import { useAuth } from "../context/AuthContext";
import {
  addFicheToFolder,
  deleteFiche,
  removeFicheFromFolder,
  setFicheArchived,
  setFicheStarred,
  subscribeMyFiches,
  type FicheDoc,
  type FicheType,
  type FicheStatus,
} from "../lib/fiches";
import { subscribeMyFolders, type FolderDoc } from "../lib/folders";
import { FOLDER_SWATCH, FOLDER_TINT } from "../components/CreateFolderDialog";

type TypeFilter = "all" | FicheType;
type ViewMode = "active" | "archived";
type GroupMode = "none" | "folder" | "city" | "dept" | "type" | "status";

// A generic grouping bucket. `icon` is an optional ReactNode rendered in the
// bucket header (folder swatch, status dot, etc.) — if omitted the header
// still renders cleanly.
type GroupBucket = {
  id: string;
  label: string;
  icon?: ReactNode;
  fiches: FicheDoc[];
};

export default function Fiches() {
  const { go } = useNav();
  const { user, loading: authLoading } = useAuth();
  const [fiches, setFiches] = useState<FicheDoc[]>([]);
  const [folders, setFolders] = useState<FolderDoc[]>([]);
  const [state, setState] = useState<"loading" | "loaded" | "anon">(
    user ? "loading" : "anon",
  );
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<TypeFilter>("all");
  const [view, setView] = useState<ViewMode>("active");
  const [group, setGroup] = useState<GroupMode>("none");
  const [selection, setSelection] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setFiches([]);
      setFolders([]);
      setState("anon");
      return;
    }
    setState("loading");
    const unsubFiches = subscribeMyFiches((list) => {
      setFiches(list);
      setState("loaded");
    });
    const unsubFolders = subscribeMyFolders(setFolders);
    return () => {
      unsubFiches();
      unsubFolders();
    };
  }, [user, authLoading]);

  // Clear selection when the underlying view changes — stale ids would stay
  // checked but invisible in the table.
  useEffect(() => {
    setSelection(new Set());
  }, [view, group, filter, query]);

  const activeFiches = useMemo(
    () => fiches.filter((f) => !f.archived),
    [fiches],
  );
  const archivedFiches = useMemo(
    () => fiches.filter((f) => f.archived),
    [fiches],
  );

  const counts = useMemo(() => {
    const mandat = activeFiches.filter((f) => f.type === "Mandat").length;
    const acheteur = activeFiches.filter((f) => f.type === "Acheteur").length;
    return {
      total: activeFiches.length,
      archived: archivedFiches.length,
      mandat,
      acheteur,
    };
  }, [activeFiches, archivedFiches]);

  const folderById = useMemo(() => {
    const map = new Map<string, FolderDoc>();
    for (const f of folders) map.set(f.id, f);
    return map;
  }, [folders]);

  const sourcePool = view === "archived" ? archivedFiches : activeFiches;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sourcePool.filter((f) => {
      if (filter !== "all" && f.type !== filter) return false;
      if (!q) return true;
      const hay = [
        f.id,
        f.title,
        f.geocode?.label,
        f.geocode?.city,
        f.geocode?.postcode,
        f.zone?.libelle,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [sourcePool, query, filter]);

  // Smart views — compute buckets according to the current grouping mode.
  // Returns a flat list of buckets; folder mode can emit several buckets for
  // a single fiche (multi-folder membership is the whole point of the tags).
  const buckets = useMemo<GroupBucket[] | null>(() => {
    if (group === "none") return null;
    if (group === "folder") {
      const byFolder = new Map<string, FicheDoc[]>();
      const orphans: FicheDoc[] = [];
      for (const f of visible) {
        if (!f.folderIds || f.folderIds.length === 0) {
          orphans.push(f);
          continue;
        }
        for (const fid of f.folderIds) {
          const arr = byFolder.get(fid) ?? [];
          arr.push(f);
          byFolder.set(fid, arr);
        }
      }
      const list: GroupBucket[] = folders
        .filter((fo) => byFolder.has(fo.id))
        .map((fo) => ({
          id: `folder:${fo.id}`,
          label: fo.name,
          icon: (
            <span className={FOLDER_TINT[fo.color]}>
              {fo.icon ? (
                <span className="text-[13px]">{fo.icon}</span>
              ) : (
                <Ic.Folder size={12} />
              )}
            </span>
          ),
          fiches: byFolder.get(fo.id)!,
        }));
      if (orphans.length > 0) {
        list.push({
          id: "folder:__none__",
          label: "Sans dossier",
          icon: <Ic.Folder size={12} className="text-ink-400" />,
          fiches: orphans,
        });
      }
      return list;
    }
    // Generic key-based grouping for city / dept / type / status.
    const bucketKey = (f: FicheDoc): { id: string; label: string } => {
      if (group === "city") {
        const city = f.geocode?.city?.trim();
        return city ? { id: city, label: city } : { id: "__na__", label: "Sans ville" };
      }
      if (group === "dept") {
        const pc = f.geocode?.postcode?.trim();
        const dept = pc && pc.length >= 2 ? pc.slice(0, 2) : null;
        return dept
          ? { id: dept, label: `${dept} — ${DEPT_LABELS[dept] ?? "Département"}` }
          : { id: "__na__", label: "Sans département" };
      }
      if (group === "type") return { id: f.type, label: f.type };
      // status
      const statusLabel =
        f.status === "brouillon"
          ? "Brouillon"
          : f.status === "envoyé"
            ? "Envoyé"
            : "Signé";
      return { id: f.status, label: statusLabel };
    };
    const statusIcon = (status: FicheStatus) => {
      const color =
        status === "signé"
          ? "bg-forest"
          : status === "envoyé"
            ? "bg-ink-400"
            : "bg-amber-400";
      return <span className={`w-1.5 h-1.5 rounded-full ${color}`} />;
    };
    const map = new Map<string, { label: string; fiches: FicheDoc[] }>();
    for (const f of visible) {
      const { id, label } = bucketKey(f);
      const existing = map.get(id) ?? { label, fiches: [] };
      existing.fiches.push(f);
      map.set(id, existing);
    }
    return Array.from(map.entries())
      .map(([id, { label, fiches }]) => ({
        id: `${group}:${id}`,
        label,
        icon:
          group === "status"
            ? statusIcon(id as FicheStatus)
            : group === "type"
              ? <Ic.File size={12} className="text-ink-400" />
              : group === "city"
                ? <Ic.Pin size={12} className="text-ink-400" />
                : <Ic.Grid size={12} className="text-ink-400" />,
        fiches,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, "fr"));
  }, [visible, folders, group]);

  function toggleSelect(id: string, checked: boolean) {
    setSelection((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }
  function selectAll(ids: string[], checked: boolean) {
    setSelection((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }

  const visibleIds = useMemo(() => visible.map((f) => f.id), [visible]);
  const allSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selection.has(id));
  const selectedFiches = useMemo(
    () => fiches.filter((f) => selection.has(f.id)),
    [fiches, selection],
  );

  return (
    <AppShell active="fiches">
      <TopHeader>
        <Button variant="soft" size="sm" icon={<Ic.Filter size={13} />}>
          Filtrer
        </Button>
      </TopHeader>

      <button
        onClick={() => go("nouvelle")}
        className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-40 h-10 sm:h-12 px-4 sm:px-5 rounded-full bg-ink-900 text-white flex items-center gap-2 shadow-pop hover:bg-ink-800 text-[13px] sm:text-[13.5px]"
      >
        <Ic.Plus size={16} /> Nouvelle analyse
      </button>

      <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-6 md:py-8 page-enter">
        {/* Heading */}
        <div className="flex items-end justify-between gap-4 sm:gap-6 flex-wrap mb-6 sm:mb-8">
          <div>
            <SectionLabel>Archive</SectionLabel>
            <h1 className="mt-2 text-[24px] sm:text-[34px] font-semibold tracking-tight">
              Mes fiches{" "}
              <span className="font-serif italic font-normal text-ink-500">
                ·
              </span>{" "}
              <span className="text-ink-600 font-normal">
                {counts.total === 0
                  ? "aucune fiche pour l’instant."
                  : counts.total === 1
                    ? "1 fiche enregistrée."
                    : `${counts.total} fiches enregistrées.`}
              </span>
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Pill tone="soft" icon={<Ic.Dot size={8} className="text-forest" />}>
              Synchronisées
            </Pill>
            <Button variant="soft" size="sm" icon={<Ic.Download size={13} />}>
              Exporter
            </Button>
          </div>
        </div>

        {/* View + filters row */}
        <Card className="p-3 mb-4 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Ic.Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher par adresse, zone, identifiant…"
              className="w-full bg-ink-50 rounded-full pl-9 pr-3 h-9 text-[13px] outline-none focus:ring-2 focus:ring-lime-300"
            />
          </div>
          <div className="flex items-center gap-1 bg-ink-100 rounded-full p-1 text-[12px]">
            <FilterChip
              label={`Toutes · ${counts.total}`}
              active={filter === "all"}
              onClick={() => setFilter("all")}
            />
            <FilterChip
              label={`Mandat · ${counts.mandat}`}
              active={filter === "Mandat"}
              onClick={() => setFilter("Mandat")}
            />
            <FilterChip
              label={`Acheteur · ${counts.acheteur}`}
              active={filter === "Acheteur"}
              onClick={() => setFilter("Acheteur")}
            />
          </div>
          <div className="flex items-center gap-1 bg-ink-100 rounded-full p-1 text-[12px] flex-wrap">
            <FilterChip
              label="Liste"
              active={group === "none"}
              onClick={() => setGroup("none")}
            />
            <FilterChip
              label="Par dossier"
              active={group === "folder"}
              onClick={() => setGroup("folder")}
            />
            <FilterChip
              label="Par ville"
              active={group === "city"}
              onClick={() => setGroup("city")}
            />
            <FilterChip
              label="Par département"
              active={group === "dept"}
              onClick={() => setGroup("dept")}
            />
            <FilterChip
              label="Par type"
              active={group === "type"}
              onClick={() => setGroup("type")}
            />
            <FilterChip
              label="Par statut"
              active={group === "status"}
              onClick={() => setGroup("status")}
            />
          </div>
          <div className="flex items-center gap-1 bg-ink-100 rounded-full p-1 text-[12px]">
            <FilterChip
              label="Actives"
              active={view === "active"}
              onClick={() => setView("active")}
            />
            <FilterChip
              label={`Archivées · ${counts.archived}`}
              active={view === "archived"}
              onClick={() => setView("archived")}
            />
          </div>
        </Card>

        {/* Bulk actions toolbar — visible only when at least one row is selected */}
        {selectedFiches.length > 0 && (
          <BulkActionsBar
            selected={selectedFiches}
            folders={folders}
            view={view}
            onClear={() => setSelection(new Set())}
          />
        )}

        {/* Body */}
        <Card className="p-0 overflow-hidden">
          <div className="p-5 flex items-center justify-between border-b border-ink-200/70">
            <div>
              <div className="text-[15px] font-semibold tracking-tight">
                {view === "archived" ? "Fiches archivées" : "Historique des fiches"}
              </div>
              <div className="text-[12.5px] text-ink-500">
                {view === "archived"
                  ? "Masquées des vues par défaut — restaurez pour les remettre en circulation."
                  : "Les fiches sont conservées 12 mois. Téléchargez pour archive longue."}
              </div>
            </div>
            <div className="text-[12px] text-ink-500 font-mono">
              {visible.length} / {sourcePool.length}
            </div>
          </div>

          {state === "loading" && <LoadingState />}
          {state === "anon" && <AnonState onNew={() => go("nouvelle")} />}
          {state === "loaded" && sourcePool.length === 0 && (
            view === "archived" ? (
              <EmptyArchivedState />
            ) : (
              <EmptyState onNew={() => go("nouvelle")} />
            )
          )}
          {state === "loaded" && sourcePool.length > 0 && visible.length === 0 && (
            <NoMatchState
              onReset={() => {
                setQuery("");
                setFilter("all");
              }}
            />
          )}
          {state === "loaded" && visible.length > 0 && (
            buckets ? (
              <GroupedTable
                buckets={buckets}
                selection={selection}
                folderById={folderById}
                view={view}
                onToggle={toggleSelect}
                onSelectAll={selectAll}
                onOpen={(id) => go("fiche", { ficheId: id })}
              />
            ) : (
              <FichesTable
                fiches={visible}
                selection={selection}
                allSelected={allSelected}
                view={view}
                folderById={folderById}
                onToggle={toggleSelect}
                onSelectAll={(checked) => selectAll(visibleIds, checked)}
                onOpen={(id) => go("fiche", { ficheId: id })}
              />
            )
          )}
        </Card>
      </div>
    </AppShell>
  );
}

/* ---------- Bulk actions ---------- */

function BulkActionsBar({
  selected,
  folders,
  view,
  onClear,
}: {
  selected: FicheDoc[];
  folders: FolderDoc[];
  view: ViewMode;
  onClear: () => void;
}) {
  const [folderMenu, setFolderMenu] = useState(false);
  const [busy, setBusy] = useState(false);

  async function runAll(fn: (fiche: FicheDoc) => Promise<void>) {
    setBusy(true);
    try {
      await Promise.all(selected.map(fn));
      onClear();
    } catch (err) {
      console.error("Bulk action failed", err);
      alert("Une action a échoué — certaines fiches ont peut-être été traitées.");
    } finally {
      setBusy(false);
    }
  }

  const allStarred = selected.every((f) => f.starred);
  const count = selected.length;
  const label = `${count} fiche${count > 1 ? "s" : ""} sélectionnée${count > 1 ? "s" : ""}`;

  return (
    <div className="mb-4 rounded-xl2 bg-ink-900 text-white px-4 py-3 flex items-center gap-3 flex-wrap shadow-pop">
      <span className="text-[12.5px] font-medium">{label}</span>
      <span className="flex-1" />
      <button
        disabled={busy}
        onClick={() =>
          runAll((f) => setFicheStarred(f.id, !allStarred))
        }
        className="h-8 px-3 rounded-full bg-ink-700 hover:bg-ink-600 text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50"
      >
        <Ic.Star size={12} />
        {allStarred ? "Retirer étoile" : "Étoiler"}
      </button>
      <div className="relative">
        <button
          disabled={busy || folders.length === 0}
          onClick={() => setFolderMenu((v) => !v)}
          className="h-8 px-3 rounded-full bg-ink-700 hover:bg-ink-600 text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50"
          title={folders.length === 0 ? "Créez d'abord un dossier" : undefined}
        >
          <Ic.Folder size={12} />
          Ajouter à un dossier…
        </button>
        {folderMenu && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setFolderMenu(false)}
            />
            <div className="absolute right-0 top-10 z-50 w-56 bg-paper text-ink-900 rounded-xl2 hairline shadow-pop py-1 max-h-[320px] overflow-y-auto">
              {folders.map((fo) => (
                <button
                  key={fo.id}
                  onClick={async () => {
                    setFolderMenu(false);
                    await runAll((f) => addFicheToFolder(f.id, fo.id));
                  }}
                  className="w-full text-left px-3 py-1.5 text-[12.5px] hover:bg-ink-100 flex items-center gap-2"
                >
                  <span className={`${FOLDER_TINT[fo.color]}`}>
                    {fo.icon ? (
                      <span>{fo.icon}</span>
                    ) : (
                      <Ic.Folder size={12} />
                    )}
                  </span>
                  <span className="truncate">{fo.name}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
      {view === "archived" ? (
        <button
          disabled={busy}
          onClick={() => runAll((f) => setFicheArchived(f.id, false))}
          className="h-8 px-3 rounded-full bg-ink-700 hover:bg-ink-600 text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          <Ic.Archive size={12} />
          Restaurer
        </button>
      ) : (
        <button
          disabled={busy}
          onClick={() => runAll((f) => setFicheArchived(f.id, true))}
          className="h-8 px-3 rounded-full bg-ink-700 hover:bg-ink-600 text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          <Ic.Archive size={12} />
          Archiver
        </button>
      )}
      <button
        onClick={onClear}
        className="h-8 px-3 rounded-full bg-transparent hover:bg-ink-800 text-[12px] text-ink-300 hover:text-white"
      >
        Annuler
      </button>
    </div>
  );
}

/* ---------- Bits ---------- */

const FilterChip = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={`px-3 h-7 rounded-full transition ${
      active
        ? "bg-paper shadow-card font-medium text-ink-900"
        : "text-ink-600 hover:text-ink-900"
    }`}
  >
    {label}
  </button>
);

const FolderChips = ({
  folderIds,
  folderById,
  ficheId,
  max = 2,
}: {
  folderIds: string[];
  folderById: Map<string, FolderDoc>;
  ficheId: string;
  max?: number;
}) => {
  const resolved = folderIds
    .map((id) => folderById.get(id))
    .filter((f): f is FolderDoc => !!f);
  if (resolved.length === 0)
    return <span className="text-[11.5px] text-ink-400">—</span>;
  const shown = resolved.slice(0, max);
  const extra = resolved.length - shown.length;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {shown.map((fo) => (
        <button
          key={fo.id}
          onClick={(e) => {
            e.stopPropagation();
            // Click on chip = remove fiche from that folder (quick action).
            void removeFicheFromFolder(ficheId, fo.id);
          }}
          title={`Retirer de « ${fo.name} »`}
          className="inline-flex items-center gap-1 px-2 h-5 rounded-full bg-ink-100 hover:bg-rose-50 text-[11px] text-ink-700"
        >
          <span className={`w-1.5 h-1.5 rounded-full ${FOLDER_SWATCH[fo.color]}`} />
          <span className="truncate max-w-[90px]">{fo.name}</span>
        </button>
      ))}
      {extra > 0 && (
        <span className="text-[11px] text-ink-500">+{extra}</span>
      )}
    </div>
  );
};

const GroupedTable = ({
  buckets,
  selection,
  folderById,
  view,
  onToggle,
  onSelectAll,
  onOpen,
}: {
  buckets: GroupBucket[];
  selection: Set<string>;
  folderById: Map<string, FolderDoc>;
  view: ViewMode;
  onToggle: (id: string, checked: boolean) => void;
  onSelectAll: (ids: string[], checked: boolean) => void;
  onOpen: (id: string) => void;
}) => (
  <div className="divide-y divide-ink-200/70">
    {buckets.map((bucket) => {
      const ids = bucket.fiches.map((f) => f.id);
      const allSelected = ids.every((id) => selection.has(id));
      return (
        <div key={bucket.id}>
          <div className="px-5 py-2.5 bg-ink-50/60 flex items-center gap-2 text-[12px]">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={(e) => onSelectAll(ids, e.target.checked)}
              className="accent-lime-500"
            />
            {bucket.icon}
            <span className="font-medium text-ink-800">{bucket.label}</span>
            <span className="text-ink-500 font-mono text-[11px]">
              {bucket.fiches.length}
            </span>
          </div>
          <FichesTable
            fiches={bucket.fiches}
            selection={selection}
            allSelected={allSelected}
            view={view}
            folderById={folderById}
            onToggle={onToggle}
            onSelectAll={(checked) => onSelectAll(ids, checked)}
            onOpen={onOpen}
            embedded
          />
        </div>
      );
    })}
  </div>
);

// Labels des départements français — utilisés pour la vue « Par département »
// (code postal → libellé). On couvre les codes à 2 chiffres (métropole +
// DOM-TOM + Corse 2A/2B). Les codes inconnus fallback sur "Département".
const DEPT_LABELS: Record<string, string> = {
  "01": "Ain", "02": "Aisne", "03": "Allier", "04": "Alpes-de-Haute-Provence",
  "05": "Hautes-Alpes", "06": "Alpes-Maritimes", "07": "Ardèche", "08": "Ardennes",
  "09": "Ariège", "10": "Aube", "11": "Aude", "12": "Aveyron", "13": "Bouches-du-Rhône",
  "14": "Calvados", "15": "Cantal", "16": "Charente", "17": "Charente-Maritime",
  "18": "Cher", "19": "Corrèze", "21": "Côte-d'Or", "22": "Côtes-d'Armor",
  "23": "Creuse", "24": "Dordogne", "25": "Doubs", "26": "Drôme", "27": "Eure",
  "28": "Eure-et-Loir", "29": "Finistère", "2A": "Corse-du-Sud", "2B": "Haute-Corse",
  "30": "Gard", "31": "Haute-Garonne", "32": "Gers", "33": "Gironde", "34": "Hérault",
  "35": "Ille-et-Vilaine", "36": "Indre", "37": "Indre-et-Loire", "38": "Isère",
  "39": "Jura", "40": "Landes", "41": "Loir-et-Cher", "42": "Loire",
  "43": "Haute-Loire", "44": "Loire-Atlantique", "45": "Loiret", "46": "Lot",
  "47": "Lot-et-Garonne", "48": "Lozère", "49": "Maine-et-Loire", "50": "Manche",
  "51": "Marne", "52": "Haute-Marne", "53": "Mayenne", "54": "Meurthe-et-Moselle",
  "55": "Meuse", "56": "Morbihan", "57": "Moselle", "58": "Nièvre", "59": "Nord",
  "60": "Oise", "61": "Orne", "62": "Pas-de-Calais", "63": "Puy-de-Dôme",
  "64": "Pyrénées-Atlantiques", "65": "Hautes-Pyrénées", "66": "Pyrénées-Orientales",
  "67": "Bas-Rhin", "68": "Haut-Rhin", "69": "Rhône", "70": "Haute-Saône",
  "71": "Saône-et-Loire", "72": "Sarthe", "73": "Savoie", "74": "Haute-Savoie",
  "75": "Paris", "76": "Seine-Maritime", "77": "Seine-et-Marne", "78": "Yvelines",
  "79": "Deux-Sèvres", "80": "Somme", "81": "Tarn", "82": "Tarn-et-Garonne",
  "83": "Var", "84": "Vaucluse", "85": "Vendée", "86": "Vienne", "87": "Haute-Vienne",
  "88": "Vosges", "89": "Yonne", "90": "Territoire de Belfort", "91": "Essonne",
  "92": "Hauts-de-Seine", "93": "Seine-Saint-Denis", "94": "Val-de-Marne",
  "95": "Val-d'Oise", "97": "DOM", "98": "Nouvelle-Calédonie / Polynésie",
};

const FichesTable = ({
  fiches,
  selection,
  allSelected,
  view,
  folderById,
  onToggle,
  onSelectAll,
  onOpen,
  embedded = false,
}: {
  fiches: FicheDoc[];
  selection: Set<string>;
  allSelected: boolean;
  view: ViewMode;
  folderById: Map<string, FolderDoc>;
  onToggle: (id: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onOpen: (id: string) => void;
  embedded?: boolean;
}) => {
  // Two-click confirm: first click arms the delete (turns the row rose and
  // shows "Confirmer ?"); second click within ~4s actually deletes. Clicks
  // anywhere else in the table clear the arm. Lighter than a modal dialog
  // and hard to trigger accidentally since the row itself no longer opens
  // the fiche once armed.
  const [armed, setArmed] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const armTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function arm(id: string) {
    setArmed(id);
    if (armTimer.current) clearTimeout(armTimer.current);
    armTimer.current = setTimeout(() => setArmed(null), 4000);
  }

  async function confirmDelete(id: string) {
    if (armTimer.current) clearTimeout(armTimer.current);
    setArmed(null);
    setDeleting(id);
    try {
      await deleteFiche(id);
    } catch (e) {
      console.error("deleteFiche failed", e);
      alert(
        "La suppression a échoué. Vérifiez votre connexion et réessayez.",
      );
    } finally {
      setDeleting(null);
    }
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        {!embedded && (
          <thead>
            <tr className="text-ink-500 text-left">
              <th className="px-3 py-3 w-[1%]">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  className="accent-lime-500"
                />
              </th>
              <th className="px-5 py-3 font-medium">ID</th>
              <th className="px-5 py-3 font-medium">Adresse</th>
              <th className="px-5 py-3 font-medium">Dossiers</th>
              <th className="px-5 py-3 font-medium">Type</th>
              <th className="px-5 py-3 font-medium">Date</th>
              <th className="px-5 py-3 font-medium">Zone</th>
              <th className="px-5 py-3 font-medium">Potentiel</th>
              <th className="px-5 py-3 font-medium">Statut</th>
              <th className="px-5 py-3 font-medium w-[1%]"></th>
            </tr>
          </thead>
        )}
        <tbody>
          {fiches.map((f) => {
            const potential = computePotentialM2(f);
            const potText =
              potential == null
                ? "—"
                : potential > 0
                  ? `+${potential} m²`
                  : "+0 m²";
            const isArmed = armed === f.id;
            const isDeleting = deleting === f.id;
            const isSelected = selection.has(f.id);
            return (
              <tr
                key={f.id}
                onClick={() => {
                  if (isArmed) {
                    setArmed(null);
                    return;
                  }
                  onOpen(f.id);
                }}
                className={`border-t border-ink-200/70 cursor-pointer transition-colors ${
                  isArmed
                    ? "bg-rose-50/80"
                    : isDeleting
                      ? "opacity-50"
                      : isSelected
                        ? "bg-lime-50/60"
                        : "hover:bg-ink-50/70"
                }`}
              >
                <td
                  className="px-3 py-3 w-[1%]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => onToggle(f.id, e.target.checked)}
                    className="accent-lime-500"
                  />
                </td>
                <td className="px-5 py-3 font-mono text-[11.5px] text-ink-500">
                  <div className="flex items-center gap-1.5">
                    {f.starred && (
                      <Ic.Star size={10} className="text-lime-500" />
                    )}
                    {f.id}
                  </div>
                </td>
                <td className="px-5 py-3">
                  <div className="font-medium truncate max-w-[320px]">
                    {primaryAddress(f)}
                  </div>
                  <div className="text-[11.5px] text-ink-500 truncate max-w-[320px]">
                    {secondaryAddress(f)}
                  </div>
                </td>
                <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                  <FolderChips
                    folderIds={f.folderIds}
                    folderById={folderById}
                    ficheId={f.id}
                  />
                </td>
                <td className="px-5 py-3">
                  <Pill tone={f.type === "Mandat" ? "lime" : "soft"}>
                    {f.type}
                  </Pill>
                </td>
                <td className="px-5 py-3 text-ink-600">
                  {formatDate(f.createdAt?.toDate() ?? null)}
                </td>
                <td className="px-5 py-3 font-mono">
                  {f.zone?.libelle ?? "—"}
                </td>
                <td className="px-5 py-3 font-semibold tabular">{potText}</td>
                <td className="px-5 py-3">
                  <StatusDot status={f.status} />
                </td>
                <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                  {isArmed ? (
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <span className="text-[11.5px] text-rose-800 font-medium">
                        Supprimer ?
                      </span>
                      <button
                        onClick={() => confirmDelete(f.id)}
                        disabled={isDeleting}
                        className="h-7 px-2.5 rounded-full bg-rose-600 text-white text-[11.5px] font-medium hover:bg-rose-700 disabled:opacity-50"
                      >
                        Confirmer
                      </button>
                      <button
                        onClick={() => setArmed(null)}
                        className="h-7 px-2.5 rounded-full bg-ink-100 text-ink-700 text-[11.5px] hover:bg-ink-200"
                      >
                        Annuler
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <IconBtn
                        icon={
                          <Ic.Star
                            size={13}
                            className={f.starred ? "text-lime-500" : ""}
                          />
                        }
                        label={f.starred ? "Retirer de l'épingle" : "Épingler"}
                        onClick={() => setFicheStarred(f.id, !f.starred)}
                      />
                      <IconBtn
                        icon={<Ic.Archive size={13} />}
                        label={
                          view === "archived" ? "Restaurer" : "Archiver"
                        }
                        onClick={() =>
                          setFicheArchived(f.id, view !== "archived")
                        }
                      />
                      <IconBtn
                        icon={<Ic.Eye size={13} />}
                        label="Voir"
                        onClick={() => onOpen(f.id)}
                      />
                      <IconBtn
                        icon={<Ic.Trash size={13} />}
                        label="Supprimer"
                        tone="danger"
                        onClick={() => arm(f.id)}
                      />
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const StatusDot = ({ status }: { status: FicheStatus }) => {
  const color =
    status === "signé"
      ? "bg-forest"
      : status === "envoyé"
        ? "bg-ink-400"
        : "bg-amber-400";
  const label =
    status === "brouillon" ? "Brouillon" : status === "envoyé" ? "Envoyé" : "Signé";
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-1.5 h-1.5 rounded-full ${color}`} />
      <span className="text-ink-700">{label}</span>
    </span>
  );
};

const IconBtn = ({
  icon,
  label,
  onClick,
  tone = "default",
}: {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  tone?: "default" | "danger";
}) => (
  <button
    title={label}
    onClick={onClick}
    className={`w-7 h-7 rounded-full grid place-items-center transition-colors ${
      tone === "danger"
        ? "text-ink-500 hover:bg-rose-100 hover:text-rose-700"
        : "text-ink-600 hover:bg-ink-100"
    }`}
  >
    {icon}
  </button>
);

/* ---------- States ---------- */

const LoadingState = () => (
  <div className="p-14 grid place-items-center">
    <div className="text-center">
      <div className="w-14 h-14 rounded-full bg-ink-50 hairline grid place-items-center mx-auto animate-pulse">
        <Ic.File size={18} className="text-ink-500" />
      </div>
      <div className="mt-3 text-[13px] text-ink-500">
        Chargement de vos fiches…
      </div>
    </div>
  </div>
);

const EmptyState = ({ onNew }: { onNew: () => void }) => (
  <div className="p-14 text-center">
    <div className="w-14 h-14 rounded-full bg-lime-200 grid place-items-center mx-auto">
      <Ic.Wand size={18} />
    </div>
    <h2 className="mt-4 text-[22px] font-semibold tracking-tight">
      Aucune fiche pour le moment
    </h2>
    <p className="mt-2 text-[13px] text-ink-600 max-w-[460px] mx-auto leading-relaxed">
      Lancez une première analyse pour générer une fiche Potentiel. Elle
      apparaîtra ici dès qu’elle sera enregistrée.
    </p>
    <div className="mt-5 flex items-center justify-center gap-2">
      <Button variant="dark" onClick={onNew} iconRight={<Ic.Arrow size={13} />}>
        Nouvelle analyse
      </Button>
    </div>
  </div>
);

const EmptyArchivedState = () => (
  <div className="p-14 text-center">
    <div className="w-14 h-14 rounded-full bg-ink-50 hairline grid place-items-center mx-auto">
      <Ic.Archive size={18} className="text-ink-500" />
    </div>
    <h2 className="mt-4 text-[18px] font-semibold tracking-tight">
      Aucune fiche archivée
    </h2>
    <p className="mt-2 text-[12.5px] text-ink-600 max-w-[420px] mx-auto">
      Les fiches archivées apparaîtront ici — elles restent conservées mais
      masquées des vues par défaut.
    </p>
  </div>
);

const AnonState = ({ onNew }: { onNew: () => void }) => (
  <div className="p-14 text-center">
    <div className="w-14 h-14 rounded-full bg-ink-50 hairline grid place-items-center mx-auto">
      <Ic.Cross size={16} className="text-ink-500" />
    </div>
    <h2 className="mt-4 text-[22px] font-semibold tracking-tight">
      Connexion requise
    </h2>
    <p className="mt-2 text-[13px] text-ink-600 max-w-[460px] mx-auto leading-relaxed">
      Connectez-vous pour retrouver vos fiches. Chaque analyse est enregistrée
      dans votre espace sécurisé.
    </p>
    <div className="mt-5 flex items-center justify-center gap-2">
      <Button variant="dark" onClick={onNew} iconRight={<Ic.Arrow size={13} />}>
        Nouvelle analyse
      </Button>
    </div>
  </div>
);

const NoMatchState = ({ onReset }: { onReset: () => void }) => (
  <div className="p-14 text-center">
    <div className="w-14 h-14 rounded-full bg-ink-50 hairline grid place-items-center mx-auto">
      <Ic.Search size={16} className="text-ink-500" />
    </div>
    <h2 className="mt-4 text-[18px] font-semibold tracking-tight">
      Aucun résultat
    </h2>
    <p className="mt-2 text-[12.5px] text-ink-600 max-w-[420px] mx-auto">
      Aucune fiche ne correspond à vos filtres actuels.
    </p>
    <div className="mt-4">
      <Button variant="soft" size="sm" onClick={onReset}>
        Réinitialiser
      </Button>
    </div>
  </div>
);

/* ---------- Helpers ---------- */

function computePotentialM2(f: FicheDoc): number | null {
  const empriseMaxPct = f.reglement?.emprise_au_sol_max_pct;
  const surface = f.parcel?.surface_m2;
  const existingEmprise = f.existing?.emprise_m2 ?? null;
  if (empriseMaxPct == null || !surface) return null;
  const maxEmprise = (empriseMaxPct / 100) * surface;
  if (existingEmprise == null) return Math.round(maxEmprise);
  const residual = maxEmprise - existingEmprise;
  return residual > 0 ? Math.round(residual) : 0;
}

function primaryAddress(f: FicheDoc): string {
  const label = f.geocode?.label ?? "";
  if (f.geocode?.city && label.includes(f.geocode.city)) {
    const idx = label.indexOf(f.geocode.city);
    return label.slice(0, idx).replace(/[,\s]+$/, "").trim() || label;
  }
  return label || f.title || f.id;
}

function secondaryAddress(f: FicheDoc): string {
  const parts = [f.geocode?.city, f.geocode?.postcode].filter(Boolean);
  return parts.join(" ");
}

function formatDate(d: Date | null): string {
  if (!d) return "à l’instant";
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
